import { t as coreT } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { format } from 'date-fns';

import { Formik, FormikHelpers } from 'formik';
import _ from 'lodash';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { TeamPlayerForm } from 'modules/teams/ui/components/team-card/styles';
import { useState } from 'react';
import { usePreventNavigation } from 'shared/hooks';
import { useImageUpscaleFieldOption } from 'shared/hooks/use-image-upscale-access';
import { birthDateSchema } from 'shared/validators/date.validator';
import { UDFormDropzone } from 'ud-form/components/image-dropzone';
import UDFormInput from 'ud-form/components/input';
import { birthDateFormatter } from 'ud-form/components/input/domain/formatters/birth-date-formatter';
import UDButton from 'ud-ui/components/button';
import * as yup from 'yup';
import { Player } from 'modules/players/domain/interfaces/player';

interface FormValues {
  id?: number;
  surname: string;
  name: string;
  middleName?: string;
  birthDate: string;
  height?: string;
  weight?: string;
  photo?: (File | string)[];
}

/** Возвращает промис запроса: по нему «Сохранить» заперта до ответа (IMGM-1561). */
export type SubmitPlayerForm = ({
  values,
  formikHelpers,
}: {
  values: FormValues;
  formikHelpers: FormikHelpers<FormValues>;
}) => Promise<unknown>;

interface FormProps {
  onSubmit: SubmitPlayerForm;
  initialValues?: Player;
  isLoading?: boolean;
  /**
   * Отмена формы. Задаётся, когда форма живёт не на своей странице, а в модалке:
   * уходить с адреса некуда — окно просто закрывают, и перехватывать навигацию
   * (`usePreventNavigation`) тоже незачем, иначе несохранённые поля начнут
   * блокировать переходы уже закрытого окна.
   */
  onCancel?: () => void;
  /** Подпись кнопки сохранения: в модалке действие называют по имени («Добавить игрока»). */
  submitLabel?: string;
}

// Сообщения — функции: yup зовёт их в момент валидации, с текущим языком.
export const teamPlayerValidator = yup.object().shape({
  name: yup.string().required(() => coreT`Обязательное поле`),
  surname: yup.string().required(() => coreT`Обязательное поле`),
  birthDate: birthDateSchema,
});

const PlayerForm = (props: FormProps) => {
  const { t } = useLingui();
  const photoUpscale = useImageUpscaleFieldOption('photo');
  const { onSubmit, initialValues: initialPlayer, isLoading: isLoadingForm, onCancel, submitLabel } = props;

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [leavingConfirmed, setLeavingConfirmed] = useState(false);

  const { backHandler, PreventNavigationModal } = usePreventNavigation({
    shouldPrevent: !onCancel && isFormDirty && !leavingConfirmed,
    onConfirm: () => {
      setLeavingConfirmed(true);
      setIsFormDirty(false);
    },
    defaultPath: Pages.PLAYERS.INDEX,
  });

  const initialValues: FormValues = initialPlayer
    ? {
        id: initialPlayer.id,
        surname: initialPlayer.surname,
        name: initialPlayer.name,
        middleName: initialPlayer.middleName,
        birthDate: initialPlayer.birthDate ? format(new Date(initialPlayer.birthDate), 'dd.MM.yyyy') : '',
        height: initialPlayer.height?.toString(),
        weight: initialPlayer.weight?.toString(),
        photo: initialPlayer.photo ? [initialPlayer.photo.externalUrl] : [],
      }
    : {
        surname: '',
        name: '',
        middleName: '',
        birthDate: '',
      };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={teamPlayerValidator}
      onSubmit={(values, formikHelpers) => {
        // Текущие значения — база сразу: уход после создания не должен ловить «несохранённые правки».
        // `isSubmitting: true` переживает сброс — кнопку держит промис запроса, Formik снимет флаг сам.
        formikHelpers.resetForm({ values, isSubmitting: true });
        setIsFormDirty(false);
        return onSubmit({ values, formikHelpers });
      }}
      validate={(values) => {
        const dirty = !_.isEqual(values, initialValues);
        if (dirty !== isFormDirty && !leavingConfirmed) {
          setIsFormDirty(dirty);
        }
        return {};
      }}
    >
      {(formProps) => {
        return (
          <form className="flex h-full flex-col justify-between" onSubmit={formProps.handleSubmit}>
            <TeamPlayerForm>
              <div className="row">
                <div className="w-1/2" style={{ paddingRight: 24 }}>
                  <UDFormInput name="surname" label={t`Фамилия`} placeholder={t`Введите фамилию`} />

                  <div className="player-data">
                    <UDFormInput name="name" label={t`Имя`} placeholder={t`Введите имя`} />

                    <UDFormInput name="middleName" label={t`Отчество`} placeholder={t`Введите отчество`} />
                  </div>

                  {/* Единственное поле в ряду — на всю ширину колонки, как «Фамилия» выше,
                      а не в половину, как в паре с соседом (иначе плейсхолдер обрезается). */}
                  <div className="player-data" style={{ gridTemplateColumns: '1fr' }}>
                    <UDFormInput
                      name="birthDate"
                      label={t`Дата рождения`}
                      placeholder={t`Укажите дату рождения`}
                      mask={t({ message: 'дд.мм.гггг', comment: 'Маска поля даты: день, месяц, год' })}
                      formatter={birthDateFormatter}
                      parser={birthDateFormatter}
                    />
                  </div>

                  <div className="player-data">
                    <UDFormInput name="height" label={t`Рост игрока`} placeholder={t`Укажите рост`} />

                    <UDFormInput name="weight" label={t`Вес игрока`} placeholder={t`Укажите вес`} />
                  </div>
                </div>

                <div className="w-1/2" style={{ paddingLeft: 24 }}>
                  <UDFormDropzone name="photo" label={t`Фото игрока`} crop removable upscale={photoUpscale} className="p-6" />
                </div>
              </div>

              <div className="form-buttons w-1/2 pr-6">
                <UDButton
                  type="submit"
                  variant="primary"
                  disabled={isLoadingForm || !formProps.isValid}
                  // Загрузка данных карточки — запрос идёт не отсюда; свой сабмит запирает Formik.
                  loading={isLoadingForm}
                  className="mr-4 w-1/2"
                >
                  {submitLabel ?? t`Сохранить`}
                </UDButton>

                <UDButton
                  type="button"
                  variant="secondary"
                  disabled={isLoadingForm || formProps.isSubmitting}
                  onClick={onCancel ?? backHandler}
                  className="w-1/2"
                >
                  <Trans>Отменить</Trans>
                </UDButton>
              </div>
            </TeamPlayerForm>

            <PreventNavigationModal />
          </form>
        );
      }}
    </Formik>
  );
};

export default PlayerForm;
